import { HTMLAttributes } from "react";
import { cn } from "@/utils/cn";

interface TrustProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  description?: string;
  logos: Array<{
    src: string;
    alt: string;
  }>;
}

export const Trust = ({
  title = "Trusted by leading companies",
  description = "Join thousands of satisfied customers who trust our platform",
  logos,
  className,
  ...props
}: TrustProps) => {
  return (
    <section className={cn("py-16 bg-gray-50", className)} {...props}>
      <div className="container mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">{title}</h2>
          {description && (
            <p className="text-lg text-gray-600 hidden md:block">
              {description}
            </p>
          )}
        </div>

        {/* Mobile view (hidden on md and above screens) */}
        <div className="md:hidden max-w-6xl mx-auto">
          <div className="grid grid-cols-3 gap-6 justify-items-center mx-auto">
            {logos.map((logo, index) => (
              <div
                key={`mobile-${index}`}
                className={cn(
                  "flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-200",
                  /* If this is the last item and we have a remainder when dividing by 3, center it */
                  index === logos.length - 1 && logos.length % 3 === 1 && "col-span-3",
                  /* If we have 2 items in the last row, make the last two items span appropriately */
                  index >= logos.length - 2 && logos.length % 3 === 2 && "col-span-3 sm:col-span-1"
                )}
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  className="h-8 max-w-full"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Desktop view (hidden on small screens) */}
        <div className="hidden md:flex md:flex-col md:items-center max-w-6xl mx-auto">
          {/* First row - 5 logos */}
          <div className="flex flex-wrap justify-center gap-8 md:gap-12 mb-12 w-full">
            {logos.slice(0, 5).map((logo, index) => (
              <div
                key={index}
                className="flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-200"
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  className="h-10 max-w-[150px] object-contain"
                />
              </div>
            ))}
          </div>

          {/* Second row - 5 logos */}
          <div className="flex flex-wrap justify-center gap-8 md:gap-12 mb-12 w-full">
            {logos.slice(5, 10).map((logo, index) => (
              <div
                key={index + 5}
                className="flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-200"
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  className="h-10 max-w-[150px] object-contain"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-8">
          <p className="text-lg text-gray-600">and many more...</p>
        </div>
      </div>
    </section>
  );
};